import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import CaptchaWidget from "@/components/base/CaptchaWidget";
import { getCategories, getLocations, getPlans } from "@/services/publicApi";
import { submitForm, FORM_ENDPOINTS } from "@/services/forms";
import { firstProblem, validateFormData } from "@/utils/validation";
import { SITE } from "@/config/site";
import type { Category, Location, Plan } from "@/types";

const MAX_DESC = 500;

const benefits = [
  { icon: "ri-eye-line", title: "Get discovered", text: "Appear in category and location searches from customers near you." },
  { icon: "ri-shield-check-line", title: "Build trust", text: "Verified badges and reviews help customers choose you with confidence." },
  { icon: "ri-line-chart-line", title: "Track performance", text: "See views, calls and enquiries from your owner dashboard." },
  { icon: "ri-customer-service-2-line", title: "Local support", text: "Our Mysore-based team helps you set up and optimise your listing." },
];

export default function ListYourBusiness() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [desc, setDesc] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  useEffect(() => {
    Promise.all([getCategories(), getLocations(), getPlans()])
      .then(([c, l, p]) => {
        setCategories(c);
        setLocations(l);
        setPlans(p);
      })
      .catch(() => undefined);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const problem = firstProblem(validateFormData(new FormData(form), { emailRequired: true, phoneRequired: true }));
    if (problem) {
      setStatus("error");
      setMessage(problem);
      return;
    }
    setStatus("submitting");
    setMessage("");
    const result = await submitForm(
      FORM_ENDPOINTS.listBusiness,
      new FormData(form),
      "Thanks! Your business enquiry has been received. Our team will reach out within one working day.",
      { captchaAction: "list_business" },
    );
    if (result.ok) {
      setStatus("success");
      setMessage(result.message);
      form.reset();
      setDesc("");
    } else {
      setStatus("error");
      setMessage(result.message);
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";
  const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500";

  return (
    <>
      <Seo
        title="List Your Business — Free Directory Listing"
        description="Add your business to Mysore Yellow Pages for free. Reach local customers across Mysore, Mandya, Chamarajanagar and Kodagu, and upgrade for verified visibility."
        keywords="list business Mysore, free business listing, add business directory, local listing Karnataka"
        path="/list-your-business"
      />

      <div className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-8 md:py-10">
          <Breadcrumbs items={[{ label: "List Your Business" }]} />
          <h1 className="mt-3 font-heading text-2xl md:text-4xl font-extrabold tracking-tight text-foreground-950">
            List your business
          </h1>
          <p className="mt-2 max-w-2xl text-sm md:text-base text-foreground-600">
            It's free to get started. Fill in the details below and our team will set up your listing —
            usually within one working day.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 lg:gap-8">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="business_name" className={labelClass}>Business name *</label>
                  <input id="business_name" name="business_name" required placeholder="e.g. Chamundi Traders" className={inputClass} />
                </div>
                <div>
                  <label htmlFor="owner_name" className={labelClass}>Owner name *</label>
                  <input id="owner_name" name="owner_name" required placeholder="Your full name" className={inputClass} />
                </div>
                <div>
                  <label htmlFor="email" className={labelClass}>Email *</label>
                  <input id="email" name="email" type="email" required placeholder="you@business.com" className={inputClass} />
                </div>
                <div>
                  <label htmlFor="phone" className={labelClass}>Phone *</label>
                  <input id="phone" name="phone" required placeholder="+91 …" className={inputClass} />
                </div>
                <div>
                  <label htmlFor="category" className={labelClass}>Category *</label>
                  <select id="category" name="category" required className={inputClass} defaultValue="">
                    <option value="" disabled>Select a category</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="location" className={labelClass}>Location *</label>
                  <select id="location" name="location" required className={inputClass} defaultValue="">
                    <option value="" disabled>Select a district</option>
                    {locations.map((l) => (
                      <option key={l.id} value={l.name}>{l.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="website" className={labelClass}>Website</label>
                  <input id="website" name="website" placeholder="https:// (optional)" className={inputClass} />
                </div>
                <div>
                  <label htmlFor="plan" className={labelClass}>Interested plan</label>
                  <select id="plan" name="plan" className={inputClass} defaultValue="Free Listing">
                    {plans.map((p) => (
                      <option key={p.id} value={p.name}>{p.name}</option>
                    ))}
                    <option value="Not sure yet">Not sure yet</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="address" className={labelClass}>Business address</label>
                <input id="address" name="address" placeholder="Street, area, city, PIN" className={inputClass} />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor="description" className={labelClass}>Tell us about your business</label>
                  <span className="text-[11px] text-foreground-400">{desc.length}/{MAX_DESC}</span>
                </div>
                <textarea
                  id="description"
                  name="description"
                  rows={4}
                  maxLength={MAX_DESC}
                  value={desc}
                  onChange={(e) => setDesc(e.target.value.slice(0, MAX_DESC))}
                  placeholder="What do you offer, and what makes you different?"
                  className={`${inputClass} resize-none`}
                ></textarea>
              </div>

              <input
                type="text"
                name="website_alt"
                className="form-aux-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <CaptchaWidget />

              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  name="consent"
                  value="yes"
                  required
                  className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-background-300 accent-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-1"
                />
                <span className="text-xs leading-relaxed text-foreground-600">
                  I agree to the{" "}
                  <Link to="/terms-of-service" className="font-semibold text-primary-700 hover:text-primary-800 cursor-pointer">Terms of Service</Link>{" "}
                  and{" "}
                  <Link to="/privacy-policy" className="font-semibold text-primary-700 hover:text-primary-800 cursor-pointer">Privacy Policy</Link>,
                  and consent to being contacted about my listing.
                </span>
              </label>

              {status === "success" && (
                <div className="flex items-start gap-3 rounded-md border border-accent-200 bg-accent-50 p-4 text-sm text-accent-900">
                  <i className="ri-checkbox-circle-line mt-0.5 text-lg text-accent-600"></i>
                  <span>{message}</span>
                </div>
              )}
              {status === "error" && (
                <div className="flex items-start gap-3 rounded-md border border-secondary-200 bg-secondary-50 p-4 text-sm text-secondary-900">
                  <i className="ri-error-warning-line mt-0.5 text-lg text-secondary-600"></i>
                  <span>{message}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap cursor-pointer"
              >
                {status === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i> Submitting…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i> Submit listing enquiry
                  </>
                )}
              </button>
            </form>
          </div>

          <aside className="space-y-4">
            <div className="rounded-lg border border-background-200 bg-background-100/60 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">Why list with us?</h2>
              <ul className="mt-4 space-y-4">
                {benefits.map((b) => (
                  <li key={b.title} className="flex gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                      <i className={b.icon}></i>
                    </span>
                    <span>
                      <span className="block text-sm font-bold text-foreground-950">{b.title}</span>
                      <span className="mt-0.5 block text-xs text-foreground-600 leading-relaxed">{b.text}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
              <h2 className="font-heading text-sm font-bold text-foreground-950">Prefer to talk?</h2>
              <p className="mt-2 text-sm text-foreground-700">
                Call us on <a href={`tel:${SITE.phoneHref}`} className="font-semibold text-primary-800">{SITE.phone}</a> or email{" "}
                <a href={`mailto:${SITE.email}`} className="font-semibold text-primary-800 break-all">{SITE.email}</a>.
              </p>
              <Link
                to="/listing-plans"
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-800 hover:text-primary-900 cursor-pointer"
              >
                Compare listing plans <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}